import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CalendarCheck, Eraser, Loader2, Lock, RefreshCw, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, apiError } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { toast } from "sonner";

// ============================================================================
// PurgaEtapaPruebaPanel — sep-2026. Solo super_admin (el backend responde 403 real).
// La marca de arranque es INTERNA de FELVORA: vive en su propia colección de
// plataforma, no viaja en ninguna respuesta que reciba la entidad y aquí no se
// usa una sola palabra de "prueba" que el cliente pueda ver en su interfaz.
// Flujo obligatorio: fijar marca → revisar detalle (dry-run) → teclear el nombre.
// ============================================================================

const Chip = ({ tone, children, testid }) => (
  <span data-testid={testid}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
    {children}
  </span>
);

export const PurgaEtapaPruebaPanel = ({ entityId }) => {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fecha, setFecha] = useState("");
  const [motivoMarca, setMotivoMarca] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [impacto, setImpacto] = useState(null);
  const [corriendo, setCorriendo] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [frase, setFrase] = useState("");
  const [motivo, setMotivo] = useState("");
  const [purgando, setPurgando] = useState(false);
  const [sel, setSel] = useState([]);

  // Cambiar la selección invalida el detalle: la firma del dry-run va amarrada a los ids.
  const marcar = (id) => {
    setImpacto(null);
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };
  const marcarTodos = (ids) => {
    setImpacto(null);
    setSel((s) => (s.length === ids.length ? [] : ids));
  };

  const load = useCallback(async () => {
    setCargando(true);
    try {
      const { data: d } = await api.get(`/admin/entidades/${entityId}/purga-prueba/creditos`);
      setData(d);
      setFecha(d.fecha_arranque || "");
      setSel([]);
      setImpacto(null);
    } catch (e) {
      toast.error(apiError(e, "No se pudo leer el estado de la purga"));
    } finally {
      setCargando(false);
    }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const fijar = async () => {
    setGuardando(true);
    try {
      await api.put(`/admin/entidades/${entityId}/arranque-real`,
        { fecha_arranque: fecha, motivo: motivoMarca });
      toast.success("Fecha de arranque real registrada.");
      setMotivoMarca("");
      setImpacto(null);
      await load();
    } catch (e) {
      toast.error(apiError(e, "No se pudo fijar la fecha"));
    } finally {
      setGuardando(false);
    }
  };

  const dryRun = async () => {
    setCorriendo(true);
    try {
      const { data: d } = await api.post(`/admin/entidades/${entityId}/purga-prueba/dry-run`,
        { credito_ids: sel });
      setImpacto(d);
    } catch (e) {
      toast.error(apiError(e, "No se pudo calcular el impacto"));
    } finally {
      setCorriendo(false);
    }
  };

  const purgar = async () => {
    setPurgando(true);
    try {
      const { data: r } = await api.post(`/admin/entidades/${entityId}/purga-prueba/ejecutar`,
        { confirmacion: frase, motivo, firma: impacto?.firma, credito_ids: sel });
      toast.success(`Purga aplicada: ${r.folios.length} créditos y ${r.total_borrado} registros.`);
      setConfirmOpen(false);
      setFrase("");
      setMotivo("");
      setImpacto(null);
      await load();
    } catch (e) {
      toast.error(apiError(e, "No se pudo ejecutar la purga"));
    } finally {
      setPurgando(false);
    }
  };

  if (cargando) {
    return (
      <div data-testid="purga-prueba-cargando"
           className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-6 text-sm text-slate-500 shadow-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Revisando la etapa inicial de esta cuenta…
      </div>
    );
  }
  if (!data) return null;

  const esperada = data.confirmacion_esperada || "";
  const idsPurgables = data.purgables.map((c) => c.credito_id);
  const puedePurgar = frase.trim().toLowerCase() === esperada.toLowerCase() && motivo.trim().length >= 4;

  return (
    <div data-testid="purga-prueba-panel"
         className="rounded-lg border border-amber-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-100 bg-amber-50/60 px-5 py-3">
        <div>
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800">
            <Eraser className="h-4 w-4 text-amber-600" /> Depuración de la etapa inicial
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Uso interno de FELVORA. La entidad no ve esta marca ni este registro en su bitácora.
          </p>
        </div>
        <Button data-testid="purga-prueba-recargar" variant="outline" size="sm" onClick={load}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Actualizar
        </Button>
      </div>

      <div className="space-y-5 px-5 py-4">
        {/* ── Marca de arranque ── */}
        <div className="rounded-lg border border-slate-200 p-4">
          <p className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
            <CalendarCheck className="h-4 w-4 text-slate-400" /> Fecha de arranque real
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {data.fijada
              ? <>Fijada en <span data-testid="purga-prueba-marca" className="font-semibold text-slate-700">{fdate(data.fecha_arranque)}</span>. Lo capturado antes es depurable; lo posterior queda intacto.</>
              : <span data-testid="purga-prueba-sin-marca">Sin fijar: mientras no haya fecha, no hay nada depurable.</span>}
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="purga-fecha" className="text-xs">Fecha</Label>
              <Input id="purga-fecha" data-testid="purga-prueba-fecha-input" type="date"
                     className="h-9 w-44" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </div>
            <div className="min-w-[240px] flex-1">
              <Label htmlFor="purga-motivo-marca" className="text-xs">Motivo (queda en bitácora de plataforma)</Label>
              <Input id="purga-motivo-marca" data-testid="purga-prueba-motivo-marca" className="h-9"
                     placeholder="Ej.: arranque confirmado por la entidad en llamada del 3 de sep."
                     value={motivoMarca} onChange={(e) => setMotivoMarca(e.target.value)} />
            </div>
            <Button data-testid="purga-prueba-fijar-btn" size="sm" disabled={guardando || !fecha || motivoMarca.trim().length < 4}
                    onClick={fijar}>
              {guardando ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              {data.fijada ? "Actualizar fecha" : "Fijar fecha"}
            </Button>
          </div>
        </div>

        {/* ── Resumen ── */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[["Depurables", data.purgables.length, "purgables"],
            ["Bloqueados", data.bloqueados.length, "bloqueados"],
            ["Sin fecha", data.sin_fecha.length, "sin-fecha"],
            ["Posteriores (intactos)", data.posteriores, "posteriores"]].map(([l, v, t]) => (
            <div key={t} className="rounded-lg border border-slate-200 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-slate-400">{l}</p>
              <p data-testid={`purga-prueba-total-${t}`} className="font-heading text-xl font-semibold text-slate-800">{v}</p>
            </div>
          ))}
        </div>

        {data.motivo_bloqueo && (
          <p data-testid="purga-prueba-aviso-sin-marca"
             className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> {data.motivo_bloqueo}
          </p>
        )}

        {/* ── Tabla de depurables ── */}
        {data.purgables.length > 0 && (
          <div className="min-w-0 overflow-x-auto rounded-lg border border-slate-200">
            <table data-testid="purga-prueba-tabla" className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-9 px-3 py-2">
                    <Checkbox data-testid="purga-sel-todos" aria-label="Seleccionar todos"
                              checked={sel.length > 0 && sel.length === idsPurgables.length}
                              onCheckedChange={() => marcarTodos(idsPurgables)} />
                  </th>
                  {["Folio", "Fecha", "Monto", "Estatus", "Producto", "Capturó", "Dependientes"].map((h) => (
                    <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.purgables.map((c) => (
                  <tr key={c.credito_id} data-testid={`purga-fila-${c.folio}`}
                      className={`border-t border-slate-100 ${sel.includes(c.credito_id) ? "bg-amber-50/60" : ""}`}>
                    <td className="px-3 py-1.5">
                      <Checkbox data-testid={`purga-sel-${c.folio}`} aria-label={`Seleccionar ${c.folio}`}
                                checked={sel.includes(c.credito_id)}
                                onCheckedChange={() => marcar(c.credito_id)} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-medium text-slate-700">{c.folio}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">{fdate(c.fecha)}</td>
                    <td className="whitespace-nowrap px-3 py-1.5">{mxn(c.monto)}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">{c.estatus}</td>
                    <td className="px-3 py-1.5 text-slate-500">{c.producto}</td>
                    <td className="px-3 py-1.5 text-slate-500">{c.capturado_por}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">{c.dependientes_total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Bloqueados ── */}
        {data.bloqueados.length > 0 && (
          <div data-testid="purga-prueba-bloqueados" className="rounded-lg border border-red-200 bg-red-50/50 p-3">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-red-700">
              <ShieldAlert className="h-3.5 w-3.5" /> No se pueden borrar (solo cancelación lógica)
            </p>
            <ul className="mt-1.5 space-y-1 text-xs text-slate-600">
              {data.bloqueados.slice(0, 12).map((c) => (
                <li key={c.credito_id} data-testid={`purga-bloqueado-${c.folio}`}>
                  <span className="font-medium text-slate-700">{c.folio}</span> · {(c.motivos || []).join(" · ")}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Acciones ── */}
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
          <Button data-testid="purga-prueba-dryrun-btn" variant="outline" size="sm"
                  disabled={!data.fijada || corriendo || sel.length === 0} onClick={dryRun}>
            {corriendo ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Revisar detalle (sin borrar)
          </Button>
          <Button data-testid="purga-prueba-abrir-confirmar" size="sm"
                  className="bg-red-600 hover:bg-red-700"
                  disabled={!impacto || !impacto.creditos_a_purgar || sel.length === 0}
                  onClick={() => setConfirmOpen(true)}>
            <Eraser className="mr-1.5 h-3.5 w-3.5" /> Depurar definitivamente…
          </Button>
          <span data-testid="purga-prueba-seleccion" className="text-xs text-slate-500">
            {sel.length === 0
              ? "Marca en la lista los créditos que quieres depurar."
              : `${sel.length} de ${idsPurgables.length} seleccionados${impacto ? "" : " · falta revisar el detalle"}`}
          </span>
        </div>

        {/* ── Detalle del dry-run ── */}
        {impacto && (
          <div data-testid="purga-prueba-impacto" className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-xs">
            <p className="font-semibold text-slate-700">
              Se borrarían <span data-testid="purga-impacto-creditos">{impacto.creditos_a_purgar}</span> créditos
              y <span data-testid="purga-impacto-total">{impacto.total_documentos}</span> registros en total.
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {Object.entries({ ...impacto.conteos_credito, ...impacto.conteos_cliente })
                .sort((a, b) => b[1] - a[1])
                .map(([col, n]) => (
                  <Chip key={col} testid={`purga-conteo-${col}`} tone="border-slate-300 bg-white text-slate-600">
                    {col}: {n}
                  </Chip>
                ))}
              {impacto.archivos_gridfs > 0 && (
                <Chip testid="purga-conteo-gridfs" tone="border-slate-300 bg-white text-slate-600">
                  archivos: {impacto.archivos_gridfs}
                </Chip>
              )}
            </div>
            <p className="mt-2 text-slate-500">
              Cupo del plan a devolver: <span data-testid="purga-impacto-cupo" className="font-semibold">{impacto.cupo?.a_liberar || 0}</span>
              {" "}· clientes que quedarían sin operación: <span data-testid="purga-impacto-clientes">{impacto.clientes_huerfanos || 0}</span>
              {" "}· nada se borró en este paso.
            </p>
          </div>
        )}
      </div>

      {/* ── Segunda confirmación ── */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="purga-prueba-confirmar-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <AlertTriangle className="h-4 w-4 text-red-600" /> Depuración irreversible
            </DialogTitle>
            <DialogDescription>
              Se borrarán <span data-testid="purga-confirmar-cantidad" className="font-semibold text-slate-700">
              {impacto?.creditos_a_purgar}</span> de los {idsPurgables.length} créditos anteriores
              al {fdate(data.fecha_arranque)} que seleccionaste, con todo lo que cuelga de ellos
              ({impacto?.total_documentos} registros). Los folios no se reinician y no se toca
              nada posterior a esa fecha.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="purga-frase" className="text-xs">
                Escribe exactamente <span className="font-semibold text-slate-700">{esperada}</span>
              </Label>
              <Input id="purga-frase" data-testid="purga-prueba-frase" className="h-9"
                     value={frase} onChange={(e) => setFrase(e.target.value)} autoComplete="off" />
            </div>
            <div>
              <Label htmlFor="purga-motivo" className="text-xs">Motivo (obligatorio)</Label>
              <Textarea id="purga-motivo" data-testid="purga-prueba-motivo" rows={2}
                        value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="purga-prueba-cancelar" variant="outline"
                    onClick={() => setConfirmOpen(false)}>Cancelar</Button>
            <Button data-testid="purga-prueba-ejecutar" className="bg-red-600 hover:bg-red-700"
                    disabled={!puedePurgar || purgando} onClick={purgar}>
              {purgando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Borrar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
